import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { 
  CreditCard, 
  Plus, 
  Calendar,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Clock,
  TrendingUp,
  Receipt,
  Eye,
  Trash2,
  FileText,
  Edit2,
  Send
} from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

const handleViewPaymentReceipt = async (paymentId) => {
  try {
    if (!paymentId) return;
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please log in to view receipt');
      return;
    }
    const url = `${API}/payment-receipt-view/${paymentId}`;
    const response = await axios.get(url, {
      responseType: 'blob',
      headers: { Authorization: `Bearer ${token}` }
    });
    const contentType = response.headers['content-type'] || 'application/pdf';
    const blob = new Blob([response.data], { type: contentType });
    const blobUrl = window.URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
  } catch (error) {
    console.error('Error viewing receipt:', error);
    if (error.response?.status === 404) {
      toast.error('Receipt file not found');
    } else {
      toast.error('Failed to load receipt. Please try again.');
    }
  }
};

const handleViewTtReceipt = async (paymentId) => {
  try {
    if (!paymentId) return;
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please log in to view receipt');
      return;
    }
    const url = `${API}/tt-receipt-view/${paymentId}`;
    const response = await axios.get(url, {
      responseType: 'blob',
      headers: { Authorization: `Bearer ${token}` }
    });
    const contentType = response.headers['content-type'] || 'application/pdf';
    const blob = new Blob([response.data], { type: contentType });
    const blobUrl = window.URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
  } catch (error) {
    console.error('Error viewing TT receipt:', error);
    if (error.response?.status === 404) {
      toast.error('TT receipt file not found');
    } else {
      toast.error('Failed to load TT receipt. Please try again.');
    }
  }
};

const PaymentManagement = ({ user }) => {
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isPipelineMode, setIsPipelineMode] = useState(false);

  // Check if user has permission for an action
  const hasPermission = (action) => {
    // Admin has all permissions
    if (user?.role === 'admin') return true;

    // Employees: if permissions are missing (older backend), allow by default
    if (user?.role === 'employee') {
      if (!user?.permissions) return true;
      return user.permissions[action] === true;
    }

    return false;
  };

  const [formData, setFormData] = useState({
    order_id: '',
    payment_number: 1,
    amount_jpy: '',
    amount_usd: '',
    exchange_rate: '150',
    status: 'unallocated',
    notes: '',
    date: '',
    country: '',
    remitter_name: '',
    vehicle_id: '',
    // Admin-only fields
    original_amount: '',
    bank_charges: '',
    total_payment_received: ''
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [ttReceiptFile, setTtReceiptFile] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showWalletDialog, setShowWalletDialog] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [walletAmount, setWalletAmount] = useState({ usd: '', jpy: '' });
  const [walletDescription, setWalletDescription] = useState('');
  const [showCreateWalletDialog, setShowCreateWalletDialog] = useState(false);
  const [newWalletCustomerId, setNewWalletCustomerId] = useState('');
  const [newWalletAmount, setNewWalletAmount] = useState({ usd: '', jpy: '' });

  useEffect(() => {
    // Only fetch if user has permission to view payments
    if (hasPermission('payments_view')) {
      fetchOrders();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchOrders = async () => {
    try {
      const [ordersRes, paymentsRes, walletsRes, customersRes] = await Promise.all([
        axios.get(`${API}/payments/orders`),
        axios.get(`${API}/payments`),
        axios.get(`${API}/wallets`).catch(() => ({ data: [] })),
        axios.get(`${API}/customers`).catch(() => ({ data: [] }))
      ]);
      const ordersData = ordersRes.data || [];
      setOrders(ordersData);
      setWallets(walletsRes.data || []);
      setCustomers(customersRes.data || []);
      
      // Map payments to their orders for display
      const ordersById = {};
      ordersData.forEach(o => { ordersById[o.id] = o; });
      setPayments((paymentsRes.data || []).map(p => ({...p, order: ordersById[p.order_id]})));
    } catch (error) {
      console.error('Error fetching payment data:', error);
      if (error.response) {
        if (error.response.status !== 403 && error.response.status !== 404) {
          toast.error('Failed to load payment data');
        }
      } else if (error.request) {
        toast.error('Network error - Failed to connect');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Auto-calculate USD when JPY changes
      if (name === 'amount_jpy' && updated.exchange_rate) {
        const jpy = parseFloat(value) || 0;
        const rate = parseFloat(updated.exchange_rate) || 1;
        updated.amount_usd = (jpy / rate).toFixed(2);
      }
      
      // Auto-calculate JPY when USD changes
      if (name === 'amount_usd' && updated.exchange_rate) {
        const usd = parseFloat(value) || 0;
        const rate = parseFloat(updated.exchange_rate) || 1;
        updated.amount_jpy = (usd * rate).toFixed(0);
      }
      
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that at least one amount is provided
    if (!formData.amount_jpy && !formData.amount_usd) {
      toast.error('Please provide either JPY amount or USD amount');
      return;
    }
    
    try {
      const paymentData = {
        ...formData,
        order_id: formData.order_id || null, // Allow null order_id
        payment_number: formData.payment_number ? parseInt(formData.payment_number) : null,
        amount_jpy: parseFloat(formData.amount_jpy) || 0,
        amount_usd: parseFloat(formData.amount_usd) || 0,
        exchange_rate: parseFloat(formData.exchange_rate) || 1,
        // Admin-only fields (only include if filled)
        original_amount: formData.original_amount ? parseFloat(formData.original_amount) : null,
        bank_charges: formData.bank_charges ? parseFloat(formData.bank_charges) : null,
        total_payment_received: formData.total_payment_received ? parseFloat(formData.total_payment_received) : null,
        // Pipeline flag
        is_pipeline: isPipelineMode,
        // Vehicle-specific allocation
        vehicle_id: formData.vehicle_id || null
      };

      let paymentId;
      
      if (isEditMode && editingPayment) {
        // Update existing payment
        await axios.put(`${API}/payments/${editingPayment.id}`, paymentData);
        paymentId = editingPayment.id;
        toast.success('Payment updated successfully!');
      } else {
        // Create new payment
        const response = await axios.post(`${API}/payments`, paymentData);
        paymentId = response.data.id;
        toast.success(isPipelineMode ? 'Pipeline payment recorded! (No email sent)' : 'Payment recorded successfully!');
      }
      
      // Upload payment receipt if file is selected
      if (receiptFile) {
        const receiptFormData = new FormData();
        receiptFormData.append('file', receiptFile);
        
        try {
          await axios.post(`${API}/payments/${paymentId}/receipt`, receiptFormData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (receiptError) {
          console.error('Error uploading payment receipt:', receiptError);
          toast.warning('Payment saved but payment receipt upload failed');
        }
      }
      
      // Upload TT receipt if file is selected
      if (ttReceiptFile) {
        const ttFormData = new FormData();
        ttFormData.append('file', ttReceiptFile);
        
        try {
          await axios.post(`${API}/payments/${paymentId}/tt-receipt`, ttFormData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (ttError) {
          console.error('Error uploading TT receipt:', ttError);
          toast.warning('Payment saved but TT receipt upload failed');
        }
      }
      
      setShowAddDialog(false);
      setIsEditMode(false);
      setIsPipelineMode(false);
      setEditingPayment(null);
      setFormData({
        order_id: '',
        payment_number: 1,
        amount_jpy: '',
        amount_usd: '',
        exchange_rate: '150',
        status: 'unallocated',
        notes: '',
        date: '',
        country: '',
        remitter_name: '',
        vehicle_id: '',
        // Admin-only fields
        original_amount: '',
        bank_charges: '',
        total_payment_received: ''
      });
      setReceiptFile(null);
      setTtReceiptFile(null);
      
      // Optimistic update — add payment to state instantly
      if (!isEditMode && response) {
        const order = orders.find(o => o.id === paymentData.order_id);
        setPayments(prev => [{...response.data, order}, ...prev]);
      }
      // For edits, refetch in background
      if (isEditMode) {
        fetchOrders();
      }
    } catch (error) {
      console.error('Error saving payment:', error);
      const message = error.response?.data?.detail || 'Failed to save payment';
      toast.error(message);
    }
  };

  const handleEditPayment = (payment) => {
    setIsEditMode(true);
    setEditingPayment(payment);
    setIsPipelineMode(!!payment.is_pipeline);
    setFormData({
      order_id: payment.order_id || '',
      payment_number: payment.payment_number || 1,
      amount_jpy: payment.amount_jpy || '',
      amount_usd: payment.amount_usd || '',
      exchange_rate: payment.exchange_rate || '150',
      status: payment.status || 'unallocated',
      notes: payment.notes || '',
      date: payment.date || '',
      country: payment.country || '',
      remitter_name: payment.remitter_name || '',
      original_amount: payment.original_amount || '',
      bank_charges: payment.bank_charges || '',
      total_payment_received: payment.total_payment_received || ''
    });
    setShowAddDialog(true);
  };

  const handleConfirmPipeline = async (paymentId) => {
    if (!window.confirm('Confirm this pipeline payment? The customer will receive a payment notification email.')) return;
    
    try {
      const res = await axios.post(`${API}/payments/${paymentId}/confirm`);
      toast.success('Pipeline payment confirmed! Email sent to customer.');
      // Optimistic update — flip is_pipeline instantly
      setPayments(prev => prev.map(p => p.id === paymentId ? {...p, is_pipeline: false} : p));
    } catch (error) {
      console.error('Error confirming pipeline payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to confirm pipeline payment');
    }
  };

  const handleWalletCredit = async () => {
    if (!selectedWallet || (!walletAmount.usd && !walletAmount.jpy)) {
      toast.error('Please enter an amount');
      return;
    }
    try {
      await axios.post(`${API}/wallets/${selectedWallet.customer_id}/credit`, null, {
        params: {
          amount_usd: parseFloat(walletAmount.usd) || 0,
          amount_jpy: parseFloat(walletAmount.jpy) || 0,
          description: walletDescription || 'Admin credit'
        }
      });
      toast.success('Wallet credited successfully!');
      setShowWalletDialog(false);
      setSelectedWallet(null);
      setWalletAmount({ usd: '', jpy: '' });
      setWalletDescription('');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to credit wallet');
    }
  };

  const handleWalletDeduct = async () => {
    if (!selectedWallet || (!walletAmount.usd && !walletAmount.jpy)) {
      toast.error('Please enter an amount');
      return;
    }
    if (parseFloat(walletAmount.usd) > (selectedWallet.balance_usd || 0)) {
      toast.error('Insufficient wallet balance');
      return;
    }
    try {
      await axios.post(`${API}/wallets/${selectedWallet.customer_id}/deduct`, null, {
        params: {
          amount_usd: parseFloat(walletAmount.usd) || 0,
          amount_jpy: parseFloat(walletAmount.jpy) || 0,
          description: walletDescription || 'Admin deduction'
        }
      });
      toast.success('Wallet deducted successfully!');
      setShowWalletDialog(false);
      setSelectedWallet(null);
      setWalletAmount({ usd: '', jpy: '' });
      setWalletDescription('');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deduct from wallet');
    }
  };

  const handleCreateWallet = async () => {
    if (!newWalletCustomerId || (!newWalletAmount.usd && !newWalletAmount.jpy)) {
      toast.error('Select a customer and enter an amount');
      return;
    }
    try {
      await axios.post(`${API}/wallets/${newWalletCustomerId}/credit`, null, {
        params: {
          amount_usd: parseFloat(newWalletAmount.usd) || 0,
          amount_jpy: parseFloat(newWalletAmount.jpy) || 0,
          description: 'Initial wallet credit'
        }
      });
      toast.success('Wallet created and credited!');
      setShowCreateWalletDialog(false);
      setNewWalletCustomerId('');
      setNewWalletAmount({ usd: '', jpy: '' });
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create wallet');
    }
  };


  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getOverdueStatus = (order) => {
    if (!order.payment_due_date) return null;
    const daysUntil = getDaysUntilDue(order.payment_due_date);
    
    if (daysUntil === null) return null;
    
    const balance = getBalance(order);
    if (balance.usd === 0) return null; // Fully paid
    
    if (daysUntil < 0) {
      return { text: `OVERDUE ${Math.abs(daysUntil)} days`, color: 'text-red-600 font-bold' };
    } else if (daysUntil <= 10) {
      return { text: `Due in ${daysUntil} days`, color: 'text-red-600 font-semibold' };
    } else {
      return { text: `Due in ${daysUntil} days`, color: 'text-amber-600' };
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'unallocated':
        return 'bg-orange-100 text-orange-800';
      case 'unclaimed':
        return 'bg-yellow-100 text-yellow-800';
      case 'partial':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'unallocated':
        return <Clock className="h-4 w-4" />;
      case 'unclaimed':
        return <AlertCircle className="h-4 w-4" />;
      case 'partial':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Receipt className="h-4 w-4" />;
    }
  };

  const calculateOrderProgress = (order) => {
    const orderPayments = payments.filter(p => p.order_id === order.id);
    const totalPaid = orderPayments.reduce((sum, p) => sum + (p.amount_usd || 0), 0);
    const progress = (totalPaid / order.total_amount_usd) * 100;
    return Math.min(progress, 100);
  };

  const getTotalPaid = (order) => {
    const orderPayments = payments.filter(p => p.order_id === order.id);
    return {
      jpy: orderPayments.reduce((sum, p) => sum + (p.amount_jpy || 0), 0),
      usd: orderPayments.reduce((sum, p) => sum + (p.amount_usd || 0), 0)
    };
  };

  const getBalance = (order) => {
    const paid = getTotalPaid(order);
    const rate = order.exchange_rate || 150;
    // Use total paid in USD as the single source of truth (progress is based on USD)
    const totalPaidUsd = paid.usd;
    const remainingUsd = Math.max(0, order.total_amount_usd - totalPaidUsd);
    const remainingJpy = Math.round(remainingUsd * rate);
    return {
      jpy: remainingJpy,
      usd: remainingUsd
    };
  };

  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderDialog(true);
  };

  const getNextPaymentNumber = (orderId) => {
    const orderPayments = payments.filter(p => p.order_id === orderId);
    return orderPayments.length + 1;
  };

  const handleDeletePayment = async (paymentId, orderTotal) => {
    console.log('🗑️ Delete payment clicked:', paymentId);
    
    if (!window.confirm('Are you sure you want to delete this payment record? This action cannot be undone.')) {
      return;
    }

    try {
      console.log('Sending DELETE request to:', `${API}/payments/${paymentId}`);
      await axios.delete(`${API}/payments/${paymentId}`);
      toast.success('Payment deleted successfully!');
      
      // Refresh payments and orders
      await fetchOrders();
      
      // If viewing order details, refresh the selected order
      if (selectedOrder) {
        const updatedOrder = orders.find(o => o.id === selectedOrder.id);
        if (updatedOrder) {
          setSelectedOrder(updatedOrder);
        }
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || 'Failed to delete payment');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-64 mb-4"></div>
          <div className="h-4 bg-slate-200 rounded w-96"></div>
        </div>
      </div>
    );
  }

  // Show permission message if user doesn't have access
  if (!hasPermission('payments_view')) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <div className="text-center">
          <CreditCard className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Access Restricted</h2>
          <p className="text-slate-600 max-w-md">
            You don't have permission to view payments. Please contact your administrator to request access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="payment-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Payment Management</h1>
          <p className="text-slate-600">Track multiple payments and balances for customer orders</p>
        </div>
        {user?.role !== 'customer' && hasPermission('payments_edit') && (
          <div className="flex items-center gap-2">
          <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) { setIsEditMode(false); setIsPipelineMode(false); setEditingPayment(null); } }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="h-4 w-4 mr-2" />
                Record Payment
              </Button>
            </DialogTrigger>
            <DialogTrigger asChild>
              <Button 
                className="bg-amber-500 hover:bg-amber-600"
                onClick={() => { setIsPipelineMode(true); }}
              >
                <Send className="h-4 w-4 mr-2" />
                Record Pipeline Payment
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{isEditMode ? 'Edit Payment' : isPipelineMode ? 'Record Pipeline Payment' : 'Record New Payment'}</DialogTitle>
                <DialogDescription>
                  {isEditMode ? 'Update the payment details below.' : isPipelineMode ? 'Record a payment without sending email notification. Use "Confirm" later to notify the customer.' : 'Record a new payment installment for a customer order.'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="order_id">Order (Optional - can be updated later)</Label>
                  <SearchableSelect
                    value={formData.order_id}
                    onValueChange={(value) => {
                      setFormData(prev => ({
                        ...prev, 
                        order_id: value,
                        payment_number: getNextPaymentNumber(value)
                      }));
                    }}
                    options={orders.map(order => {
                      const balance = getBalance(order);
                      const stockIds = order.stock_nos?.join(', ') || 'N/A';
                      return {
                        value: order.id,
                        label: `Stock ID: ${stockIds} - Balance: $${balance.usd.toFixed(2)}`
                      };
                    })}
                    placeholder="Select order (optional)"
                    searchPlaceholder="Search orders..."
                    emptyText="No order found."
                  />
                </div>

                {/* Vehicle selector — only show for multi-vehicle orders */}
                {formData.order_id && (() => {
                  const selectedOrder = orders.find(o => o.id === formData.order_id);
                  if (selectedOrder && selectedOrder.vehicles_info && selectedOrder.vehicles_info.length > 1) {
                    return (
                      <div>
                        <Label htmlFor="vehicle_id">Allocate to Vehicle (Optional)</Label>
                        <Select 
                          value={formData.vehicle_id || '__all__'} 
                          onValueChange={(value) => setFormData({...formData, vehicle_id: value === '__all__' ? '' : value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select vehicle (or leave empty for order-level)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__all__">Order Level (All Vehicles)</SelectItem>
                            {selectedOrder.vehicles_info.map(v => (
                              <SelectItem key={v.id} value={v.id}>
                                {v.stock_no} — {v.year} {v.brand} {v.model}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500 mt-1">
                          Leave empty to apply payment to the entire order
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="payment_number">Payment # (Optional)</Label>
                    <Input
                      id="payment_number"
                      name="payment_number"
                      type="number"
                      value={formData.payment_number}
                      onChange={handleInputChange}
                      min="1"
                      max="5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="exchange_rate">Exchange Rate (JPY/USD)</Label>
                    <Input
                      id="exchange_rate"
                      name="exchange_rate"
                      type="number"
                      step="0.01"
                      value={formData.exchange_rate}
                      onChange={handleInputChange}
                      placeholder="150"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="amount_jpy">Amount (JPY) - Required if USD not provided</Label>
                    <Input
                      id="amount_jpy"
                      name="amount_jpy"
                      type="number"
                      value={formData.amount_jpy}
                      onChange={handleInputChange}
                      placeholder="500000"
                    />
                  </div>
                  <div>
                    <Label htmlFor="amount_usd">Amount (USD) - Required if JPY not provided</Label>
                    <Input
                      id="amount_usd"
                      name="amount_usd"
                      type="number"
                      step="0.01"
                      value={formData.amount_usd}
                      onChange={handleInputChange}
                      placeholder="3333.33"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="status">Payment Status</Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(value) => setFormData({...formData, status: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unallocated">Unallocated</SelectItem>
                      <SelectItem value="unclaimed">Unclaimed</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="partial">Partial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="date">Date (Optional)</Label>
                    <Input
                      id="date"
                      name="date"
                      type="date"
                      value={formData.date}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country (Optional)</Label>
                    <Input
                      id="country"
                      name="country"
                      type="text"
                      value={formData.country}
                      onChange={handleInputChange}
                      placeholder="USA, Japan, etc."
                    />
                  </div>
                  <div>
                    <Label htmlFor="remitter_name">Remitter Name (Optional)</Label>
                    <Input
                      id="remitter_name"
                      name="remitter_name"
                      type="text"
                      value={formData.remitter_name}
                      onChange={handleInputChange}
                      placeholder="Sender's name"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="payment_receipt">Payment Receipt (Optional)</Label>
                  <Input
                    id="payment_receipt"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) => setReceiptFile(e.target.files[0])}
                    className="cursor-pointer"
                  />
                  {receiptFile && (
                    <p className="text-sm text-slate-600 mt-1">
                      Selected: {receiptFile.name} ({(receiptFile.size / 1024).toFixed(1)} KB)
                    </p>
                  )}
                  <p className="text-xs text-slate-500 mt-1">
                    Accepted formats: JPG, PNG, PDF (Max 10MB)
                  </p>
                </div>

                <div>
                  <Label htmlFor="tt_receipt">TT Receipt (Optional)</Label>
                  <Input
                    id="tt_receipt"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) => setTtReceiptFile(e.target.files[0])}
                    className="cursor-pointer"
                  />
                  {ttReceiptFile && (
                    <p className="text-sm text-slate-600 mt-1">
                      Selected: {ttReceiptFile.name} ({(ttReceiptFile.size / 1024).toFixed(1)} KB)
                    </p>
                  )}
                  <p className="text-xs text-slate-500 mt-1">
                    Accepted formats: JPG, PNG, PDF (Max 10MB)
                  </p>
                </div>

                {/* Admin-only fields */}
                {user?.role === 'admin' && (
                  <>
                    <div className="col-span-full border-t pt-4 mt-2">
                      <h4 className="font-semibold text-slate-900 mb-3">Admin Only - Bank Details</h4>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="original_amount">Original Amount (USD)</Label>
                        <Input
                          id="original_amount"
                          name="original_amount"
                          type="number"
                          step="0.01"
                          value={formData.original_amount}
                          onChange={handleInputChange}
                          placeholder="5000.00"
                        />
                      </div>
                      <div>
                        <Label htmlFor="bank_charges">Bank Charges (USD)</Label>
                        <Input
                          id="bank_charges"
                          name="bank_charges"
                          type="number"
                          step="0.01"
                          value={formData.bank_charges}
                          onChange={handleInputChange}
                          placeholder="50.00"
                        />
                      </div>
                      <div>
                        <Label htmlFor="total_payment_received">Total Payment Received (USD)</Label>
                        <Input
                          id="total_payment_received"
                          name="total_payment_received"
                          type="number"
                          step="0.01"
                          value={formData.total_payment_received}
                          onChange={handleInputChange}
                          placeholder="4950.00"
                        />
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Payment method, transaction ID, etc..."
                    rows={3}
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowAddDialog(false);
                      setIsEditMode(false);
                      setIsPipelineMode(false);
                      setEditingPayment(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                    {isEditMode ? 'Update Payment' : isPipelineMode ? 'Record Pipeline Payment' : 'Record Payment'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Payment Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Orders</p>
                <p className="text-2xl font-bold text-slate-900">{orders.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Receipt className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Payments</p>
                <p className="text-2xl font-bold text-slate-900">{payments.length}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Unallocated/Unclaimed Payments</p>
                <p className="text-2xl font-bold text-slate-900">
                  {payments.filter(p => p.status === 'unallocated' || p.status === 'unclaimed').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Pipeline Payments</p>
                <p className="text-2xl font-bold text-amber-600">
                  {payments.filter(p => p.is_pipeline).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Send className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Revenue</p>
                <p className="text-2xl font-bold text-slate-900">
                  ${payments.filter(p => p.status === 'completed').reduce((sum, p) => sum + (p.amount_usd || 0), 0).toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Wallet Balance</p>
                <p className="text-2xl font-bold text-emerald-600">
                  ${wallets.reduce((sum, w) => sum + (w.balance_usd || 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customer Wallets Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-600" />
                Customer Wallets
              </CardTitle>
              <CardDescription>Customer wallet balances — click to add or deduct funds</CardDescription>
            </div>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => { setShowCreateWalletDialog(true); setNewWalletCustomerId(''); setNewWalletAmount({ usd: '', jpy: '' }); }}
            >
              <Plus className="h-4 w-4 mr-1" />
              Create Wallet
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {wallets.length === 0 ? (
            <div className="text-center py-8">
              <DollarSign className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-4">No customer wallets yet. Create one to get started.</p>
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => { setShowCreateWalletDialog(true); setNewWalletCustomerId(''); setNewWalletAmount({ usd: '', jpy: '' }); }}
              >
                <Plus className="h-4 w-4 mr-1" />
                Create First Wallet
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Balance (USD)</TableHead>
                    <TableHead>Balance (JPY)</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {wallets.map((wallet) => (
                    <TableRow key={wallet.id}>
                      <TableCell className="font-medium">{wallet.customer_name || 'Unknown'}</TableCell>
                      <TableCell className="text-sm text-slate-500">{wallet.customer_email}</TableCell>
                      <TableCell>
                        <span className={`font-semibold ${(wallet.balance_usd || 0) > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                          ${(wallet.balance_usd || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`font-semibold ${(wallet.balance_jpy || 0) > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                          ¥{(wallet.balance_jpy || 0).toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setSelectedWallet(wallet); setShowWalletDialog(true); setWalletDescription(''); setWalletAmount({ usd: '', jpy: '' }); }}
                        >
                          <Edit2 className="h-4 w-4 mr-1" />
                          Manage
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          </CardContent>
        </Card>

      {/* Wallet Management Dialog */}
      <Dialog open={showWalletDialog} onOpenChange={setShowWalletDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Wallet — {selectedWallet?.customer_name}</DialogTitle>
            <DialogDescription>
              Current balance: ${(selectedWallet?.balance_usd || 0).toFixed(2)} USD / ¥{(selectedWallet?.balance_jpy || 0).toLocaleString()} JPY
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (USD)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={walletAmount.usd}
                  onChange={(e) => setWalletAmount({...walletAmount, usd: e.target.value})}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Amount (JPY)</Label>
                <Input
                  type="number"
                  value={walletAmount.jpy}
                  onChange={(e) => setWalletAmount({...walletAmount, jpy: e.target.value})}
                  placeholder="0"
                />
              </div>
            </div>
            <div>
              <Label>Description (Optional)</Label>
              <Input
                value={walletDescription}
                onChange={(e) => setWalletDescription(e.target.value)}
                placeholder="e.g., Overpayment, refund, advance..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowWalletDialog(false)}>Cancel</Button>
              <Button 
                className="bg-red-600 hover:bg-red-700"
                onClick={handleWalletDeduct}
                disabled={parseFloat(walletAmount.usd || 0) <= 0 && parseFloat(walletAmount.jpy || 0) <= 0}
              >
                Deduct
              </Button>
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handleWalletCredit}
                disabled={parseFloat(walletAmount.usd || 0) <= 0 && parseFloat(walletAmount.jpy || 0) <= 0}
              >
                Add Funds
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Wallet Dialog */}
      <Dialog open={showCreateWalletDialog} onOpenChange={setShowCreateWalletDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Customer Wallet</DialogTitle>
            <DialogDescription>
              Select a customer and set initial balance. Wallet auto-creates on first credit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Customer</Label>
              <Select value={newWalletCustomerId} onValueChange={setNewWalletCustomerId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.full_name} ({c.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Initial Credit (USD)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newWalletAmount.usd}
                  onChange={(e) => setNewWalletAmount({...newWalletAmount, usd: e.target.value})}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Initial Credit (JPY)</Label>
                <Input
                  type="number"
                  value={newWalletAmount.jpy}
                  onChange={(e) => setNewWalletAmount({...newWalletAmount, jpy: e.target.value})}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowCreateWalletDialog(false)}>Cancel</Button>
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handleCreateWallet}
                disabled={!newWalletCustomerId || (parseFloat(newWalletAmount.usd || 0) <= 0 && parseFloat(newWalletAmount.jpy || 0) <= 0)}
              >
                Create & Fund
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Orders with Payment Status */}
      <Card>
        <CardHeader>
          <CardTitle>Orders & Payment Status</CardTitle>
          <CardDescription>Track payment progress for all orders</CardDescription>
        </CardHeader>
        <CardContent>
          {orders.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Stock ID</TableHead>
                    <TableHead>Total Amount</TableHead>
                    <TableHead>Paid Amount</TableHead>
                    <TableHead>Balance</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Payments Made</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => {
                    const paid = getTotalPaid(order);
                    const balance = getBalance(order);
                    const progress = calculateOrderProgress(order);
                    const orderPayments = payments.filter(p => p.order_id === order.id);
                    const hasPipeline = orderPayments.some(p => p.is_pipeline);

                    return (
                      <TableRow key={order.id} className={`hover:bg-slate-50 ${hasPipeline ? 'bg-amber-50/60' : ''}`}>
                        <TableCell className="font-mono text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <span>{order.stock_nos?.join(', ') || 'N/A'}</span>
                            {hasPipeline && (
                              <Badge className="bg-amber-100 text-amber-800 border-amber-200 whitespace-nowrap">
                                <Clock className="h-3 w-3 mr-1" />
                                Pipeline
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">¥{order.total_amount_jpy?.toLocaleString()}</p>
                            <p className="text-sm text-slate-500">${order.total_amount_usd?.toLocaleString()}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-green-600">¥{paid.jpy.toLocaleString()}</p>
                            <p className="text-sm text-green-500">${paid.usd.toLocaleString()}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className={`font-medium ${balance.usd === 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ¥{balance.jpy.toLocaleString()}
                            </p>
                            <p className={`text-sm ${balance.usd === 0 ? 'text-green-500' : 'text-red-500'}`}>
                              ${balance.usd.toLocaleString()}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-2 min-w-[120px]">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-slate-600">
                                {progress.toFixed(0)}%
                              </span>
                              {progress === 100 ? (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              ) : progress > 0 ? (
                                <Clock className="h-4 w-4 text-yellow-600" />
                              ) : (
                                <AlertCircle className="h-4 w-4 text-red-600" />
                              )}
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                              <div 
                                className={`h-2.5 rounded-full transition-all ${
                                  progress === 100 ? 'bg-green-600' : 
                                  progress > 50 ? 'bg-blue-600' : 
                                  progress > 0 ? 'bg-yellow-600' : 'bg-red-600'
                                }`}
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {orderPayments.length > 0 ? (
                              <>
                                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100">
                                  <span className="text-lg font-bold text-green-700">
                                    {orderPayments.length}
                                  </span>
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium text-slate-900">
                                    {orderPayments.length} {orderPayments.length === 1 ? 'payment' : 'payments'}
                                  </span>
                                  <span className="text-xs text-slate-500">
                                    {orderPayments.filter(p => p.status === 'completed').length} completed
                                    {hasPipeline && ` · ${orderPayments.filter(p => p.is_pipeline).length} pipeline`}
                                  </span>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100">
                                  <span className="text-lg font-bold text-red-700">0</span>
                                </div>
                                <span className="text-sm text-slate-500">No payments yet</span>
                              </>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => viewOrderDetails(order)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <CreditCard className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Orders Found</h3>
              <p className="text-slate-500">Create some orders first to start tracking payments.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Details Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Payment Details</DialogTitle>
            <DialogDescription>
              Complete payment breakdown for this order
            </DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-slate-900 mb-3">Order Summary</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Stock ID:</span> {selectedOrder.stock_nos?.join(', ') || 'N/A'}</p>
                    <p><span className="font-medium">Total:</span> ¥{selectedOrder.total_amount_jpy?.toLocaleString()} / ${selectedOrder.total_amount_usd?.toLocaleString()}</p>
                    <p><span className="font-medium">Progress:</span> {calculateOrderProgress(selectedOrder).toFixed(1)}% paid</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-slate-900 mb-3">Payment Summary</h4>
                  <div className="space-y-2 text-sm">
                    {(() => {
                      const paid = getTotalPaid(selectedOrder);
                      const balance = getBalance(selectedOrder);
                      const overdueStatus = getOverdueStatus(selectedOrder);
                      return (
                        <>
                          <p><span className="font-medium">Paid:</span> <span className="text-green-600 font-semibold">¥{paid.jpy.toLocaleString()} / ${paid.usd.toLocaleString()}</span></p>
                          <p><span className="font-medium">Balance:</span> <span className="text-red-600 font-semibold">¥{balance.jpy.toLocaleString()} / ${balance.usd.toLocaleString()}</span></p>
                          {selectedOrder.payment_due_date && (
                            <p><span className="font-medium">Due Date:</span> {new Date(selectedOrder.payment_due_date).toLocaleDateString()}</p>
                          )}
                          {overdueStatus && (
                            <p className={overdueStatus.color}>⚠️ {overdueStatus.text}</p>
                          )}
                          <p><span className="font-medium">Payments:</span> {payments.filter(p => p.order_id === selectedOrder.id).length} installments</p>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Payment History</h4>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Remitter</TableHead>
                        <TableHead>Receipts</TableHead>
                        <TableHead>Notes</TableHead>
                        {hasPermission('payments_edit') && <TableHead>Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payments
                        .filter(p => p.order_id === selectedOrder.id)
                        .sort((a, b) => a.payment_number - b.payment_number)
                        .map((payment) => (
                          <TableRow key={payment.id}>
                            <TableCell>
                              <div>
                                <span className="font-medium">#{payment.payment_number}</span>
                                {selectedOrder?.vehicles_info && selectedOrder.vehicles_info.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {payment.vehicle_id ? (
                                      (() => {
                                        const v = selectedOrder.vehicles_info.find(vi => vi.id === payment.vehicle_id);
                                        return v ? (
                                          <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                                            {v.stock_no}
                                          </span>
                                        ) : null;
                                      })()
                                    ) : (
                                      selectedOrder.vehicles_info.map(v => (
                                        <span key={v.id} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                          {v.stock_no}
                                        </span>
                                      ))
                                    )}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p>¥{payment.amount_jpy?.toLocaleString()}</p>
                                <p className="text-sm text-slate-500">${payment.amount_usd?.toLocaleString()}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Badge className={getPaymentStatusColor(payment.status)}>
                                  {payment.status}
                                </Badge>
                                {payment.is_pipeline && (
                                  <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                                    Pipeline
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">
                              {payment.date || new Date(payment.payment_date).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-sm">{payment.remitter_name || '-'}</TableCell>
                            <TableCell className="text-sm">
                              <div className="flex items-center gap-2">
                                {payment.payment_receipt ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleViewPaymentReceipt(payment.id)}
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-1 h-auto"
                                    title="View payment receipt"
                                  >
                                    <FileText className="h-4 w-4" />
                                  </Button>
                                ) : ''}
                                {payment.tt_receipt ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleViewTtReceipt(payment.id)}
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-1 h-auto"
                                    title="View TT receipt"
                                  >
                                    <FileText className="h-4 w-4" />
                                  </Button>
                                ) : ''}
                                {!payment.payment_receipt && !payment.tt_receipt && '-'}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">{payment.notes || '-'}</TableCell>
                            {hasPermission('payments_edit') && (
                              <TableCell>
                                <div className="flex items-center space-x-1">
                                  {payment.is_pipeline && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleConfirmPipeline(payment.id)}
                                      className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                      title="Confirm & send email to customer"
                                    >
                                      <Send className="h-4 w-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditPayment(payment)}
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    title="Edit payment"
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeletePayment(payment.id, selectedOrder.total_amount_usd)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    title="Delete payment"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentManagement;
